import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  Optional,
  Inject,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { Observable, Subject, combineLatest } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  tap,
  catchError,
  take,
  map,
} from 'rxjs/operators';

import { IPurchaseOrderMultiselectLineItem } from '@app/interfaces/purchase-order.model';
import { CostLayersApiService } from '@app/modules/cost-layers/services/cost-layers-api.service';
import { ReceivingReportUnifiedFormComponent } from 'v2/modules/receiving-report/components/receiving-report-unified-form/receiving-report-unified-form.component';
import { DialogConfig } from 'v2/modules/shared/configs/dialog.config';

// COMPONENTS
import { DecimalPlacesPipe } from 'v2/modules/shared/pipes/decimal-places.pipe';
import { LumResponseError } from 'v2/shared/api/lum-response-error';

import { ICost } from '../../../pricing/interfaces/pricing.interface';
import { RfqKitItemsAddFormComponent } from '../../../rfq/components/rfq-kit-items-add-form/rfq-kit-items-add-form.component';

// CONFIGS
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// INTERFACES
import { IWarehouse } from '../../../shared/interfaces/location-warehouse.interface';
import {
  PricingApiService,
  LocationWarehouseApiService,
  OrderTransferApiService,
  SystemSettingApiService,
  RfqApiService,
} from '../../../shared/services/api';
import { RfqUtilService } from '../../../shared/services/util/rfq-util.service';
import {
  formatDateToISOString,
  parseUTCStringToDateISO,
} from '../../../shared/util/date.utils';
import { trackById } from '../../../shared/util/track-by.util';
import { IUnitOfMeasure } from '../../../unit-of-measure/interfaces/unit-of-measure.interface';
import {
  IOrderTransfer,
  EOrderTransferStatus,
  ISalesOrderItem,
} from '../../interfaces/order-transfer.interface';

// SERVICES

// UTILS
import { EItemType } from '../../interfaces/order.interface';

//PIPES

import { DecimalPlacesUtils } from './../../../shared/util/decimal-places.utils';

@Component({
  selector: 'lum-order-transfer-form',
  templateUrl: './order-transfer-form.component.html',
  styleUrls: ['./order-transfer-form.component.scss'],
})
export class OrderTransferFormComponent implements OnInit, OnDestroy {
  @Output()
  bulkReceiveEvent = new EventEmitter<any>();

  loading$ :Observable<boolean>;
  warehouses$ :Observable<IWarehouse[]>;
  warehouses :IWarehouse[] = [];
  filteredWarehouses :IWarehouse[] = [];
  unitCostByRfq$ :Observable<{
    [key :string] :ICost;
  }>;
  unitCostByRfq :{
    [key :string] :ICost;
  } = {};
  submittingForm$ :Observable<boolean>;
  currentOrder$ :Observable<IOrderTransfer | null>;
  currentOrder :IOrderTransfer;
  initialValues :any = null; // TODO: Create type
  settings :{
    warehouse_group_id ?:number;
  } = {};
  rfqLocationStockLoading :boolean = false;
  isLoading$ :Observable<boolean>;

  form :UntypedFormGroup;
  orderTransferStatus = EOrderTransferStatus;
  relations :string = [
    'orderItems.item.unitOfMeasures',
    'channel',
  ].join(',');

  trackById = trackById;

  isModal :boolean = false;
  stockGridItemIds :string;

  private onDestroy$ = new Subject();

  constructor(
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private locationWarehouseApiService :LocationWarehouseApiService,
    private rfqUtilService :RfqUtilService,
    private orderTransferApiService :OrderTransferApiService,
    private pricingApiService :PricingApiService,
    private snackBar :MatSnackBar,
    private router :Router,
    private route :ActivatedRoute,
    private systemSettingsService :SystemSettingApiService,
    private rfqApiService :RfqApiService,
    private dialog :MatDialog,
    @Optional() public dialogRef :MatDialogRef<OrderTransferFormComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData :any,
    private costLayersApiService :CostLayersApiService,
    private decimalPlaces :DecimalPlacesPipe,
  ) {
    if (this.dialogRef && dialogData && dialogData.isModal) {
      this.isModal = dialogData.isModal;
      this.stockGridItemIds = dialogData.stockGridItemIds;
    }
  }

  ngOnInit() :void {
    this.initLoading();
    this.initActions();
    this.initFormValues();
    this.initForm();
    this.getRouterParams().subscribe();
    this.getFromService();
    this.getWarehouses().subscribe();
    this.getCurrentOrder().subscribe();
    this.listenToLineItemsChange();
    this.getDefaultWarehouseGroupId();
    this.listenToSourceWarehouseChange();

    if (this.isModal) {
      this.processEachSKUItem();
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  private processEachSKUItem() :void {
    const isInitItems :boolean = true;
    this.rfqApiService
      .searchAsync({
        rfqIds: this.stockGridItemIds,
        relations:
          'category,subcategory,basePrice,tags.tag_details,images,locationStock,variantParent.category,variantParent.subcategory,variantParent.tags.tag_details,variantParent.unitOfMeasures,rfqAttachments,unitOfMeasures,quotations',
        per_page: '9999',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (resp :any) => {
          resp.forEach((item) => {
            this.onAddLineItem({ item: item });
          });
        },
        (error) => {
          // Handle error
        },
      );
  }

  onAddLineItem(data :{ item :any; index ?:number }) :void {
    const { item, index } = data;
    const itemGroup = this.rfqUtilService.getItemFormGroup(item);

    if (index !== undefined) {
      this.lineItemsFormArray.setControl(index, itemGroup);
    } else {
      // Find the index where the empty/default values end
      let insertIndex = this.lineItemsFormArray.length;
      let emptyCount = 0;
      for (let i = this.lineItemsFormArray.length - 1; i >= 0; i--) {
        const control = this.lineItemsFormArray.at(i);
        const value = control.value;
        if (value.product_name !== '') {
          break;
        }
        emptyCount++;
      }

      if (emptyCount > 0) {
        insertIndex = this.lineItemsFormArray.length - emptyCount;
      }

      this.lineItemsFormArray.insert(insertIndex, itemGroup);
    }

    this.processUnitCosting(item);
  }

  private processUnitCosting(item :any) :void {
    if (item) {
      this.getRfqUnitCost(item);
    }
  }

  onRemoveLineItem(index :number) :void {
    this.lineItemsFormArray.removeAt(index);
    setTimeout(() => {
      this.cd.detectChanges();
    }, 500);
  }

  onTransfer(orderStatus :EOrderTransferStatus) :void {
    const { value } = this.form;
    const {
      orderDate,
      arrivalDate,
      note,
      shippingMethod,
      shippingCost,
      line_items,
      orderNumber,
      sourceWarehouse,
      destinationWarehouse,
      autoReceiveOrderOnArrival,
      channel,
    } = value;
    this.form.markAllAsTouched();

    try {
      // TODO: CREATE A GLOBAL VALIDATION TRIGGER
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        this.form.updateValueAndValidity();
        throw new Error('Please fill all the required fields.');
      }

      // Make line items required
      if (
        !this.lineItemsFormArray?.value?.filter((item :any) => item.sku)?.length
      ) {
        this.snackBar.open('Please add at least one line item.', 'Fail', {
          duration: SnackBarConfig.DEFAULT.duration,
        });

        return;
      }

      const payload = {
        orderNumber,
        sourceWarehouseId: sourceWarehouse?.id,
        destinationWarehouseId: destinationWarehouse?.id,
        orderDate: formatDateToISOString(orderDate),
        arrivalDate: formatDateToISOString(arrivalDate),
        note,
        shippingMethod,
        shippingCost,
        itemsCost: this.itemsCost,
        orderStatus,
        autoReceiveOrderOnArrival,
        channelId: channel?.id,
        orderItems: line_items
          .filter((item :any) => item.sku)
          .map((item :any) => {
            const { description, quantity, ordered_uom_id, item_id } = item;

            return {
              description: description || '',
              quantity,
              orderedUomId: ordered_uom_id,
              rfqId: item_id,
            };
          }),
      };

      if (this.currentOrder) {
        this.orderTransferApiService
          .update({
            id: this.currentOrder.id,
            payload,
          })
          .pipe(
            takeUntil(this.onDestroy$),
            tap((transferOrder :any) => {
              this.snackBar.open('Transfer order successfully updated', 'Close', {
                duration: 2000,
              });

              this.onCreateRecievingReport(transferOrder, true);
            }),
            catchError(({ error }) :any => {
              this.snackBar.open(
                error ?? 'Please fill all the required fields.',
                'Fail',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );
            }),
          )
          .subscribe();
      } else {
        this.orderTransferApiService
          .create(payload)
          .pipe(
            takeUntil(this.onDestroy$),
            tap((transferOrder :any) => {
              this.snackBar.open('Transfer order successfully created', 'Close', {
                duration: 2000,
              });

              if (!this.isModal) {
                this.onCreateRecievingReport(transferOrder, true);
              } else {
                this.dialogRef.close(true);
              }
            }),
            catchError(({ error }) :any => {
              this.snackBar.open(
                error ?? 'Please fill all the required fields.',
                'Fail',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );
            }),
          )

          .subscribe();
      }
    } catch (error :any) {
      this.snackBar.open(error, 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
    }
  }

  displayWarehouseFn(option :IWarehouse) :string {
    return option && option.location_name ? option.location_name : '';
  }

  onSelectRfqFromFormTable(data :{ item :any; tempId :number }) :void {
    const { item, tempId } = data;
    const index = this.lineItemsFormArray.controls.findIndex(
      (control :AbstractControl) => control.value.item_id === tempId,
    );
    if (item?.type === EItemType.KIT) {
      // Add delay in order to focus next input then popup after
      setTimeout(() => {
        this.handleKitItemDialog(item);
        this.lineItemsFormArray.removeAt(index);
      }, 600);
    } else {
      this.onAddLineItem({ item, index });
    }
  }

  onSelectWarehouse(event :MatAutocompleteSelectedEvent, type :string) :void {
    this.form.get(type)?.setValue(event.option.value);
    this.filteredWarehouses = this.warehouses;
  }

  onSearchWarehouseChange(event :any, type :string) :void {
    const name = event.target.value;
    if (name) {
      this.filteredWarehouses = this.warehouses.filter((warehouse :IWarehouse) =>
        warehouse.location_name.toLowerCase().includes(name.toLowerCase()),
      );
    } else {
      this.filteredWarehouses = this.warehouses;
      this.form.get(type)?.setValue(null);
    }
  }

  onAddRowItems(count :number) :void {
    for (let i = 0; i < count; i++) {
      this.lineItemsFormArray.push(this.rfqUtilService.emptyItemRow());
    }
  }

  private handleKitItemDialog(item :IPurchaseOrderMultiselectLineItem) :void {
    const dialogRef = this.dialog.open(RfqKitItemsAddFormComponent, {
      width: DialogConfig.sizes['extra-large'],
      data: { kit: item },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result && result?.length > 0) {
          result.forEach((kitItem) => {
            this.onAddLineItem({ item: kitItem });
          });
        }
      });
  }

  private initActions() :void {
    this.orderTransferApiService.setCurrentOrder(null);
    this.locationWarehouseApiService.fetchWarehouses({
      all: '1',
      parent_id: '0',
      withWarehouseGroup: 'true',
      hasWarehouseGroup: 'true',
    });
  }

  private getFromService() :void {
    this.loading$ = this.orderTransferApiService.loading$;
    this.warehouses$ = this.locationWarehouseApiService.warehouses$;
    this.unitCostByRfq$ = this.pricingApiService.unitCostByRfq$;
    this.submittingForm$ = this.orderTransferApiService.submitting$;
    this.currentOrder$ = this.orderTransferApiService.currentOrder$;
  }

  private initFormValues() :void {
    let sourceWarehouse = null;
    let destinationWarehouse = null;

    if (this.currentOrder && this.warehouses?.length) {
      sourceWarehouse = this.warehouses.find(
        (warehouse :IWarehouse) =>
          warehouse.id === this.currentOrder?.sourceWarehouseId,
      );

      destinationWarehouse = this.warehouses.find(
        (warehouse :IWarehouse) =>
          warehouse.id === this.currentOrder?.destinationWarehouseId,
      );
    }

    let lineItems :UntypedFormArray = this.fb.array([]);
    let defaultItemRows = 5;

    const currentorderItems = this.currentOrder?.orderItems || [];
    if (this.currentOrder && this.currentOrder.orderItems.length) {
      lineItems = this.fb.array(
        currentorderItems.map((orderItem :ISalesOrderItem) => {
          const {
            id,
            description,
            item,
            internalSku,
            productName,
            quantity,
            unitCost,
            lineItemCost,
            orderedUomId,
          } = orderItem;

          let unit_of_measures :UntypedFormArray = new UntypedFormArray([]);

          if (item?.unitOfMeasures) {
            unit_of_measures = this.fb.array(
              item.unitOfMeasures.map((uom :IUnitOfMeasure) =>
                this.fb.group({
                  id: uom.id,
                  name: uom.name,
                }),
              ),
            );
          }

          return this.fb.group({
            id,
            description,
            item_id: item?.id,
            sku: internalSku,
            product_name: productName,
            product: {
              id: item?.id,
              sku: internalSku,
              product_name: productName,
            }, // Not included in the payload
            is_kit: 0,
            quantity: this.decimalPlaces.transform(quantity),
            original_unit_price: unitCost,
            unit_price: unitCost,
            net_total_amount: lineItemCost,
            discount_amount: 0,
            discount_percent: 0, // Not included in the payload
            discount_amount_per_unit: 0,
            markup_amount: 0,
            markup_amount_per_unit: 0,
            ordered_uom_id: orderedUomId,
            order: 0,
            unit_class_id: item?.unitClassId,
            unit_of_measures, // Not included in the payload
            stock: this.fb.control(item?.stock), // Not included in the payload
            location_stock: this.fb.control(null), // Not included in the payload
            oss_limit_notify: null, // Not included in the payload
            type: null, // Not included in the payload
            selected: false, // Not included in the payload// Not included in the payload Not included in the payload
          });
        }),
      );
    } else {
      while (defaultItemRows--) {
        lineItems.push(this.rfqUtilService.emptyItemRow());
      }
    }

    this.initialValues = {
      orderNumber: this.currentOrder?.orderNumber || null,
      sourceWarehouse,
      destinationWarehouse,
      orderDate: this.currentOrder
        ? parseUTCStringToDateISO(this.currentOrder?.orderDate)
        : DateTime.now().toISODate(),
      arrivalDate: this.currentOrder
        ? parseUTCStringToDateISO(this.currentOrder?.arrivalDate)
        : DateTime.now().toISODate(),
      note: this.currentOrder?.note || null,
      shippingMethod: this.currentOrder?.shippingMethod || null,
      shippingCost: this.currentOrder?.shippingCost || 0,
      itemsCost: Number(this.currentOrder?.itemsCost) || 0,
      autoReceiveOrderOnArrival:
        this.currentOrder?.autoReceiveOrderOnArrival || false,
      line_items: lineItems,
      channel: this.currentOrder?.channel || null,
    };
  }

  private initForm() :void {
    const {
      orderNumber,
      orderDate,
      arrivalDate,
      note,
      shippingMethod,
      shippingCost,
      line_items,
      itemsCost,
      sourceWarehouse,
      destinationWarehouse,
      autoReceiveOrderOnArrival,
      channel,
    } = this.initialValues;

    this.form = this.fb.group({
      orderNumber: [orderNumber, this.validateOrderNumber.bind(this)],
      sourceWarehouse: [sourceWarehouse, this.validateWarehouse.bind(this)],
      destinationWarehouse: [
        destinationWarehouse,
        this.validateWarehouse.bind(this),
      ],
      orderDate: [orderDate, Validators.required],
      arrivalDate: [arrivalDate, Validators.required],
      note: [note],
      shippingMethod: [shippingMethod, this.validateShippingCost.bind(this)],
      shippingCost: [shippingCost, Validators.required],
      itemsCost: [itemsCost],
      line_items,
      autoCreateRecievingReport: [false],
      autoReceiveOrderOnArrival: [autoReceiveOrderOnArrival || false],
      channel: [channel],
    });

    // update existing line item stocks on source warehouse changes
    this.sourceWarehouseControl.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((changes) => {
        this.updateLineItemStocks();
      });
  }

  private updateLineItemStocks() :void {
    const rfqIds :number[] = this.lineItemsFormArray.value
      .filter(
        (lineItemValue :any) =>
          lineItemValue.sku.length > 0 && lineItemValue.product,
      )
      .map((lineItemValue :any) => lineItemValue.product.id);

    if (!rfqIds.length) {
      return;
    }
    const params :{ [param :string] :string | string[] } = {
      relations: ['locationStock'],
      warehouse_id: this.sourceWarehouse.id.toString(),
      per_page: '1000', // TODO: Make location stocks part of the response get TO by ID.
      offset: '0',
      rfqIds: rfqIds.join(','),
    };
    this.rfqLocationStockLoading = true;
    this.rfqApiService.searchAsync(params).subscribe((response) => {
      if (response.length > 0) {
        response.forEach((rfq :any) => {
          const lineItemIndex = this.lineItemsFormArray.value.findIndex(
            (lineItem :any) => lineItem.product.id === rfq.id,
          );
          const lineItemValue = this.lineItemsFormArray.at(lineItemIndex).value;
          lineItemValue.location_stock = rfq.location_stock;
          this.lineItemsFormArray.at(lineItemIndex).patchValue(lineItemValue);
        });
      }
      this.rfqLocationStockLoading = false;
      this.cd.detectChanges();
    });
  }

  private getRouterParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const id = params['id'];
        if (id) {
          this.orderTransferApiService.subscribeGetById({
            id,
            params: { relations: this.relations },
          });
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private listenToLineItemsChange() :void {
    this.lineItemsFormArray.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe((items) => {
        this.setLineItem(items);
        this.lineItemsFormArray.markAsDirty();
      });
  }

  private listenToSourceWarehouseChange() :void {
    this.sourceWarehouseControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe((warehouse :IWarehouse) => {
        if (warehouse && this.lineItemsFormArray.length > 0) {
          this.lineItemsFormArray.value.forEach((item :any) => {
            if (item.sku) {
              if (item.id <= 0) {
                item.id = item.product?.id ?? 0;
              }
              this.getRfqUnitCost(item);
            }
          });
        }
      });
  }

  private getDefaultWarehouseGroupId() :void {
    this.systemSettingsService
      .getSettingsByKey('default_warehouse_group_id')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (warehouseGroupId :string) => {
          this.settings.warehouse_group_id = Number(warehouseGroupId);
        },
        error: () => {
          this.snackBar.open('Failed to get Warehouse Group ID.', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private onCreateRecievingReport(transferOrder :any, redirect :boolean) :void {
    if (
      this.autoCreateRecievingReportControl.value &&
      transferOrder.supplierPurchaseOrder?.purchaseOrderItems &&
      transferOrder.supplierPurchaseOrder.purchaseOrderItems.length > 0
    ) {
      if (
        this.autoCreateRecievingReportControl.value &&
        transferOrder.supplierPurchaseOrder?.purchaseOrderItems &&
        transferOrder.supplierPurchaseOrder.purchaseOrderItems.length > 0
      ) {
        this.dialog
          .open(ReceivingReportUnifiedFormComponent, {
            data: {
              supplierPurchaseOrderId: transferOrder.supplierPurchaseOrder.id,
              extraCosts: [
                {
                  name: 'Shipping Cost',
                  quantity: 1,
                  unitPrice: Number(transferOrder.shippingCost),
                },
              ],
            },
            width: DialogConfig.sizes['xxx-large'],
          })
          .afterClosed()
          .pipe(take(1))
          .subscribe((result) => {
            if (result) {
              this.router.navigate(['inventory/receiving']);
            }
          });
      }
    } else {
      if (redirect) {
        this.router.navigate(['/inventory/transfer-order/list']);
      }
    }
  }

  private getCurrentOrder() :Observable<IOrderTransfer | null> {
    return this.currentOrder$.pipe(
      tap((order) => {
        if (order) {
          this.currentOrder = order;
          this.initFormValues();
          this.initForm();
          this.updateLineItemStocks();
          this.listenToLineItemsChange();
          this.cd.detectChanges();
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  // TODO: CREATE TYPE
  private setLineItem(items :any[]) :void {
    items
      .filter((item) => item.sku)
      .forEach((item :any, index :number) => {
        const itemForm = this.lineItemsFormArray.at(index) as UntypedFormGroup;
        const quantityControl = itemForm.get('quantity');
        const itemNetTotalControl = itemForm.get('net_total_amount');
        const unitPriceControl = itemForm.get('unit_price');
        const unitMeassureControl = itemForm.get('ordered_uom_id');

        // QUANTITY CHANGE
        if (quantityControl?.dirty) {
          itemNetTotalControl?.patchValue(
            Number((quantityControl?.value * unitPriceControl?.value).toFixed(2)),
            { emitEvent: false },
          );
          quantityControl?.markAsPristine();
          itemNetTotalControl?.markAsPristine();
          this.cd.detectChanges();
        }

        // UOM CHANGE
        if (unitMeassureControl?.dirty) {
          this.pricingApiService
            .getUnitCostByRfq({
              rfqId: item?.item_id?.toString(),
              quantity: item?.quantity?.toString() || '1',
              uomId: unitMeassureControl?.value?.toString(),
              warehouseId: this.sourceWarehouseControl?.value?.id?.toString(),
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response :ICost) => {
                this.getUnitCostFromResponse(response);
                unitMeassureControl?.markAsPristine();
              },
              error: (response) => {
                this.snackBar.open(
                  response.error ?? 'Unable to get unit cost from rfq',
                  'Fail',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );
              },
            });
        }
      });
  }

  private getUnitCostFromResponse(response :ICost) :void {
    const items = this.lineItemsFormArray.value;

    if (Object.keys(response).length && response.rfqId > 0) {
      this.unitCostByRfq[response.rfqId] = response;

      if (items?.length) {
        items.forEach((item :any, index :number) => {
          const cost = this.unitCostByRfq[item.item_id];
          if (cost) {
            const itemForm = this.lineItemsFormArray.at(index) as UntypedFormGroup;
            const unitPriceControl = itemForm.get('unit_price');
            const netTotalAmountControl = itemForm.get('net_total_amount');

            const unitCost =
              cost.convertedUnitCost > 0 ? cost.convertedUnitCost : cost.unitCost;
            netTotalAmountControl?.patchValue(
              DecimalPlacesUtils.format(Number(unitCost) * item.quantity),
            );
            unitPriceControl?.patchValue(DecimalPlacesUtils.format(unitCost));
          }
        });
      }

      this.cd.detectChanges();
    }
  }

  private getWarehouses() :Observable<IWarehouse[]> {
    return this.warehouses$.pipe(
      tap((warehouses) => {
        this.warehouses = warehouses;
        this.filteredWarehouses = warehouses;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getCostLayerByRfqWarehouse(rfqId :number, warehouseId :number) {
    this.costLayersApiService
      .getCostLayerByRfqWarehouse(rfqId, warehouseId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          const items = this.lineItemsFormArray.value;

          if (items?.length > 0) {
            items.forEach((item :any, index :number) => {
              const itemForm = this.lineItemsFormArray.at(
                index,
              ) as UntypedFormGroup;
              const unitPriceControl = itemForm.get('unit_price');
              const netTotalAmountControl = itemForm.get('net_total_amount');

              if (item?.sku) {
                unitPriceControl?.patchValue(Number(response.costPerUnit));
                netTotalAmountControl?.patchValue(
                  Number(response.costPerUnit) * item.quantity,
                );
                unitPriceControl?.markAsPristine();
              }
            });
          }
          this.cd.detectChanges();
        },
        error: (error :LumResponseError) => {
          this.snackBar.open(error.message, 'Fail', {
            ...SnackBarConfig.ERROR,
          });
        },
      });
  }

  private getRfqUnitCost(item :any) :void {
    if (item) {
      const { id, quantity, default_selling_unit_id, uom_id } = item;
      const uomIdValue = uom_id ?? default_selling_unit_id;
      const ordered_uom = item.unit_of_measures.find(
        (uom :any) => uom.id === uomIdValue,
      );

      this.pricingApiService
        .getUnitCostByRfq({
          rfqId: id?.toString(),
          quantity: quantity?.toString() || '1',
          uomId: ordered_uom?.id?.toString(),
          warehouseId: this.sourceWarehouseControl?.value?.id?.toString(),
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response :ICost) => {
            this.getUnitCostFromResponse(response);
          },
          error: (response) => {
            this.snackBar.open(
              response.error ?? 'Unable to get unit cost from rfq',
              'Fail',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );
          },
        });
    }
  }

  private validateOrderNumber(control :AbstractControl) :ValidationErrors | null {
    return this.currentOrder ? Validators.required(control) : null;
  }

  private validateWarehouse(control :AbstractControl) :ValidationErrors | null {
    const warehouse = control.value;
    if (!warehouse || !warehouse.location_name) {
      return { required: true };
    }

    return null;
  }

  private validateShippingCost(control :AbstractControl) :ValidationErrors | null {
    const shippingCost = control.value;
    if (!shippingCost || shippingCost < 0) {
      return { required: true };
    }

    return null;
  }

  private initLoading() :void {
    this.isLoading$ = combineLatest([this.submittingForm$, this.loading$]).pipe(
      map(([submittingForm, loading]) => submittingForm || loading),
    );
  }

  get sourceWarehouseControl() :UntypedFormControl {
    return this.form.get('sourceWarehouse') as UntypedFormControl;
  }

  get destinationWarehouseControl() :UntypedFormControl {
    return this.form.get('destinationWarehouse') as UntypedFormControl;
  }

  get lineItemsFormArray() :UntypedFormArray {
    return this.form?.get('line_items') as UntypedFormArray;
  }

  get availableWarehouses() :IWarehouse[] {
    const sourceWarehouseId = this.sourceWarehouseControl?.value?.id;
    const destinationWarehouseId = this.destinationWarehouseControl?.value?.id;

    const filterIds = [sourceWarehouseId, destinationWarehouseId];

    if (filterIds.length) {
      return this.filteredWarehouses.filter(
        (warehouse :IWarehouse) => !filterIds.includes(warehouse.id),
      );
    }

    return this.filteredWarehouses;
  }

  get itemsCost() :number {
    return this.lineItemsFormArray.controls.reduce(
      (acc :number, control :AbstractControl) => {
        const itemNetTotalControl = control.get('net_total_amount');

        return acc + Number(itemNetTotalControl?.value);
      },
      0,
    );
  }

  get sourceWarehouse() :IWarehouse {
    return this.form.get('sourceWarehouse')?.value;
  }

  get autoCreateRecievingReportControl() :UntypedFormControl {
    return this.form.get('autoCreateRecievingReport') as UntypedFormControl;
  }
}
